import React from "react";
import trans from "../../../utils";
import PopupBodyItem from "../../Helpers/Popup/PopupParts/PopupBodyItem";
import { Image, Tag } from "antd";
import { formatDateString } from "../../../JsHelpers/DateFormat";
import { Icon } from "leaflet";
import { MapContainer, Marker, TileLayer } from "react-leaflet";
import ContactNumberList from "../../CallCenter/ContactNumberList/ContactNumberList";
import { getColorByValue } from "../../CallCenter/FormCallCenter/serviceRequestColors";

const formatList = (items, lang = "en") => {
  if (!Array.isArray(items) || items.length === 0) return "-";
  return items
    .map((item) => {
      if (typeof item === "string") return item;
      if (lang === "ar") return item?.nameAr || item?.name || item?.id;
      return item?.name || item?.id;
    })
    .filter(Boolean)
    .join(", ");
};

const getItemColor = (index) => {
  const colors = [
    "blue",
    "green",
    "orange",
    "purple",
    "cyan",
    "magenta",
    "volcano",
    "gold",
    "lime",
    "geekblue",
  ];
  return colors[index % colors.length];
};

const renderColoredTags = (items, lang = "en") => {
  if (!Array.isArray(items) || items.length === 0) return "-";
  return items.map((item, index) => {
    const name =
      typeof item === "string"
        ? item
        : lang === "ar"
        ? item?.nameAr || item?.name || item?.id
        : item?.name || item?.id;
    return (
      <Tag key={index} color={getItemColor(index)} style={{ marginBottom: "4px" }}>
        {name}
      </Tag>
    );
  });
};

const ViewRequestBody = ({ data }) => {
  if (!data) return;

  Icon.Default.mergeOptions({
    iconUrl: "https://www.svgrepo.com/show/513317/location-pin.svg",
  });

  const lang = localStorage.getItem("lang") === "ar" ? "ar" : "en";
  const requestedServices = data?.formDetails?.requestedServices;
  const otherServiceText = data?.formDetails?.otherServiceText;
  const hasFormDetails =
    Array.isArray(requestedServices) && requestedServices.length > 0;

  const contactRows = [
    { key: "landline", kind: "landline", value: data?.poi?.landline || "" },
    ...(Array.isArray(data?.poi?.secondaryPhones) ? data.poi.secondaryPhones : []).map(
      (phone, index) => ({ key: `secondary-${index}`, kind: "secondary", index, value: phone }),
    ),
  ];
  const defects = data?.defects || data?.issueNames || [];
  const spareParts = data?.spareParts || data?.sparePartNames || [];

  const statusColors = {
    inProgress: `blue`,
    pending: `orange`,
    completed: `green`,
    cancelled: `red`,
    none: `default`,
    Assigned: `purple`,
  };

  return (
    <>
      <p className="fs-16 fw-500 brand-700 text-uppercase">
        {trans("Request Details")}
      </p>
      <div className="row">
        <div className="col-lg-12">
          <div className="display-border">
            <PopupBodyItem
              title={trans("Description")}
              item={data?.description}
            />
          </div>
        </div>
        <div className="col-lg-6">
          <div className="display-border">
            <PopupBodyItem
              title={trans("status")}
              item={
                <Tag
                  color={statusColors[data.status] || statusColors.none}
                  className="text-capitalize"
                >
                  {trans(data.status)}
                </Tag>
              }
            />
            <PopupBodyItem
              title={trans("Submitted By")}
              item={trans(data?.userName)}
            />
            <PopupBodyItem
              title={trans("preferred time")}
              item={
                data?.preferredTime &&
                (data?.preferredTimeEnd
                  ? `${formatDateString(data.preferredTime)} - ${formatDateString(data.preferredTimeEnd)}`
                  : formatDateString(data.preferredTime))
              }
            />
          </div>
        </div>
        <div className="col-lg-6">
          <div className="display-border">
            <PopupBodyItem
              title={trans("created at")}
              item={data?.createdAt && formatDateString(data?.createdAt)}
            />

            <PopupBodyItem
              title={trans("Email")}
              item={trans(data?.userEmail)}
            />
          </div>
        </div>
        {data?.photos?.length > 0 && (
          <div className="col-lg-12 mt-3">
            <p className="fw-500 mb-2">{trans("Photos")}</p>
            <Image.PreviewGroup>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
                  gap: "10px",
                }}
              >
                {data.photos.map((photo, index) => (
                  <Image
                    key={index}
                    src={photo?.url}
                    alt={`photo-${index}`}
                    style={{
                      width: "100%",
                      height: "100px",
                      objectFit: "cover",
                      borderRadius: "8px",
                    }}
                    fallback="https://via.placeholder.com/150?text=No+Image"
                    onError={(e) => {
                      e.target.onerror = null; // prevent loop
                      e.target.src =
                        "https://via.placeholder.com/150?text=Image+Not+Found";
                    }}
                  />
                ))}
              </div>
            </Image.PreviewGroup>
          </div>
        )}
      </div>

      {(hasFormDetails || data?.inspectionNeeded || otherServiceText) && (
        <>
          <p className="fs-16 fw-500 brand-700 text-uppercase mt-3">
            {trans("Requested Services")}
          </p>
          <div className="row">
            <div className="col-lg-12">
              <div className="display-border">
                <PopupBodyItem
                  title={trans("Site Inspection Required")}
                  item={
                    <Tag color={data?.inspectionNeeded ? "green" : "default"}>
                      {trans(data?.inspectionNeeded ? "Yes" : "No")}
                    </Tag>
                  }
                />
              </div>
            </div>

            {hasFormDetails &&
              requestedServices.map((service, index) => (
                <div className="col-lg-6" key={index}>
                  <div className="display-border">
                    <p className="fw-500 mb-2">
                      {service?.category?.[lang]}
                    </p>
                    <ul className="mb-0 ps-3">
                      {service?.items?.map((item, itemIndex) => (
                        <li key={itemIndex}>{item?.[lang]}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}

            {otherServiceText && (
              <div className="col-lg-12">
                <div className="display-border">
                  <PopupBodyItem
                    title={trans("Other Service")}
                    item={otherServiceText}
                  />
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {data?.poi && (
        <>
          <p className="fs-16 fw-500 brand-700 text-uppercase mt-3">
            {lang === "ar" ? "العميل والموقع" : "Customer & Location"}
          </p>
          <div className="row">
            <div className="col-lg-6">
              <div className="display-border">
                <PopupBodyItem
                  title={lang === "ar" ? "اسم العميل" : "Customer Name"}
                  item={data.poi.name}
                />
              </div>
              <div className="display-border mt-2">
                <ContactNumberList
                  primaryNumber={data.poi.phoneNumber || ""}
                  matchedNumber={data.callerPhoneNumber ?? null}
                  rows={contactRows}
                  editable={false}
                />
              </div>
            </div>
            <div className="col-lg-6">
              <div className="display-border">
                <PopupBodyItem title={lang === "ar" ? "المحافظة" : "Governorate"} item={trans(data.poi.state)} />
                <PopupBodyItem title={lang === "ar" ? "المركز" : "Markaz"} item={trans(data.poi.city)} />
                <PopupBodyItem title={lang === "ar" ? "القرية" : "Village"} item={data.poi.village} />
              </div>
            </div>
            {(data?.sku || data?.model) && (
              <div className="col-lg-12">
                <div className="display-border">
                  <PopupBodyItem
                    title={lang === "ar" ? "المنتج" : "Product"}
                    item={data.sku?.skuName}
                  />
                  <PopupBodyItem
                    title={lang === "ar" ? "الموديل" : "Model"}
                    item={data.model?.modelName}
                  />
                  <PopupBodyItem
                    title={lang === "ar" ? "اتصل قبل التحرك" : "Call Before Performing"}
                    item={
                      <Tag color={data.callBeforePerforming ? "green" : "default"}>
                        {lang === "ar" ? (data.callBeforePerforming ? "نعم" : "لا") : (data.callBeforePerforming ? "Yes" : "No")}
                      </Tag>
                    }
                  />
                  <PopupBodyItem
                    title={lang === "ar" ? "لون المنتج" : "Model Color"}
                    item={(() => {
                      const colorInfo = getColorByValue(data.color);
                      const colorLabel = colorInfo
                        ? lang === "ar"
                          ? colorInfo.labelAr
                          : colorInfo.labelEn
                        : data.color;
                      return data.color ? (
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <span
                            style={{
                              display: "inline-block",
                              width: "20px",
                              height: "20px",
                              borderRadius: "50%",
                              backgroundColor: colorInfo?.color || data.color,
                              border: "1px solid #d9d9d9",
                            }}
                          />
                          <span style={{ textTransform: "capitalize" }}>{colorLabel}</span>
                        </div>
                      ) : (
                        "-"
                      );
                    })()}
                  />
                  <PopupBodyItem
                    title={lang === "ar" ? "عيوب / مشاكل" : "Defects / Issues"}
                    item={renderColoredTags(defects, lang)}
                  />
                  <PopupBodyItem
                    title={lang === "ar" ? "قطع غيار" : "Spare Parts"}
                    item={renderColoredTags(spareParts, lang)}
                  />
                </div>
              </div>
            )}
            {data.poi.latitude && data.poi.longitude && (
              <div className="col-lg-12 mt-2">
                <MapContainer
                  center={[data.poi.latitude, data.poi.longitude]}
                  zoom={15}
                  style={{ height: "260px", width: "100%", borderRadius: "8px" }}
                >
                  <TileLayer url="https://mt0.google.com/vt/lyrs=m&x={x}&y={y}&z={z}" />
                  <Marker position={[data.poi.latitude, data.poi.longitude]} />
                </MapContainer>
                {data.poi.address && (
                  <p style={{ color: "#2E9245", marginTop: "6px" }}>
                    {data.poi.address}
                  </p>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default ViewRequestBody;
